import { EditRecordButton } from "@/components/explorer/RecordEditor";
import { useEffect, useState } from "react";
import { PageHeader } from "@/components/shared/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { adapter } from "@/services/db";
import LegalDocuments from "@/components/system/LegalDocuments";
import LeadSettings from "@/components/system/LeadSettings";
import { getProfilePwStatus, verifyProfilePw } from "@/services/ccApi";
import { resetOtp } from "@/services/otp";
import { ProfilePasswordOtp } from "@/components/system/ProfilePasswordOtp";

export default function SystemControl() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);        // system_status/global doc
  const [pwSet, setPwSet] = useState(null);          // is profile password defined
  const [unlocked, setUnlocked] = useState(false);
  const [busy, setBusy] = useState(false);

  // unlock form
  const [pw, setPw] = useState("");
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    const unsub = adapter.subscribeDoc("system_status", "global", setStatus);
    getProfilePwStatus().then((r) => setPwSet(r.is_set)).catch(() => setPwSet(false));
    return () => { unsub && unsub(); resetOtp(); };
  }, []);

  const masterOn = status ? status.master_enabled !== false : true; // default ON if unset

  const unlock = async () => {
    setBusy(true);
    try { const r = await verifyProfilePw(pw); if (r.ok) { setUnlocked(true); toast.success("Unlocked"); } else toast.error("Incorrect profile password"); }
    catch (e) { toast.error(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); setPw(""); }
  };

  const toggleMaster = async (val) => {
    setBusy(true);
    try {
      await adapter.set("system_status", "global", { master_enabled: val, updated_at: new Date().toISOString(), updated_by: user?.email });
      toast.success(val ? "System ENABLED — apps are live" : "System DISABLED — apps show maintenance");
    } catch (e) {
      toast.error(e?.code === "permission-denied" ? "Publish the system_status rule first (see chat)" : (e.message || "Failed"));
    } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="System Control" subtitle="Master switch for the entire Brugo ecosystem. When OFF, brugo-web and brugo-home show a maintenance screen and sign-in/sign-up is paused. The Command Center itself always stays usable." />
      <div id="recaptcha-container" />

      {/* Status banner */}
      <Card className={`p-5 mb-6 border-2 ${masterOn ? "border-emerald-200 bg-emerald-50/40" : "border-amber-300 bg-amber-50/50"}`} data-testid="system-status-banner">
        <div className="flex items-center gap-3">
          <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${masterOn ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
            <Icons.Power className="h-5 w-5" />
          </div>
          <div>
            <p className="font-heading font-bold text-lg text-foreground">{masterOn ? "System is LIVE" : "Maintenance mode is ON"}</p>
            <p className="text-sm text-muted-foreground">{masterOn ? "All consumer apps and features are active." : "Consumer apps show the “almost ready” screen; sign-in/sign-up paused."}</p>
          </div>
        </div>
      </Card>

      {!unlocked ? (
        <Card className="p-6 max-w-md" data-testid="system-lock">
          <div className="flex items-center gap-2 mb-4"><Icons.Lock className="h-5 w-5 text-primary" /><h3 className="font-heading font-semibold text-foreground">Profile password required</h3></div>

          {pwSet === false ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">No profile password is set yet. Create one.</p>
              <ProfilePasswordOtp mode="set" onDone={() => { setPwSet(true); setUnlocked(true); }} />
            </div>
          ) : changing ? (
            <>
              <ProfilePasswordOtp mode="change" onDone={() => setChanging(false)} />
              <Button variant="ghost" size="sm" className="mt-2" onClick={() => setChanging(false)} data-testid="change-pw-back">Back to unlock</Button>
            </>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Profile password</Label><Input type="password" data-testid="unlock-pw" value={pw} onChange={(e) => setPw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && unlock()} /></div>
              <Button className="w-full gap-1.5" onClick={unlock} disabled={busy} data-testid="unlock-btn"><Icons.Unlock className="h-4 w-4" /> Unlock</Button>
              <Button variant="link" size="sm" className="px-0" onClick={() => setChanging(true)} data-testid="change-pw-link">Forgot or want to change the profile password?</Button>
            </div>
          )}
        </Card>
      ) : (
        <div className="flex flex-wrap gap-6 items-start">
        <Card className="p-6 max-w-md" data-testid="master-switch-card">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-heading font-semibold text-foreground flex items-center gap-1">Master switch <EditRecordButton path="system_status" id="global" /></h3>
              <p className="text-sm text-muted-foreground mt-1">{masterOn ? "Everything is ON" : "Everything is OFF"}</p>
            </div>
            <Switch checked={masterOn} onCheckedChange={toggleMaster} disabled={busy} data-testid="master-switch" />
          </div>
          <div className="mt-4 rounded-lg border border-border bg-secondary/50 p-3 text-xs text-muted-foreground">
            Turning this OFF sets <span className="font-mono">system_status/global.master_enabled = false</span>. brugo-web and brugo-home read this and show the maintenance screen (including their sign-in/sign-up).
          </div>
          <Button variant="ghost" size="sm" className="mt-3 text-muted-foreground" onClick={() => setUnlocked(false)} data-testid="relock-btn"><Icons.Lock className="h-4 w-4 mr-1" /> Lock again</Button>
        </Card>
        <Card className="p-6 max-w-md w-full" data-testid="change-pw-card">
          <div className="flex items-center gap-2 mb-3"><Icons.KeyRound className="h-5 w-5 text-primary" /><h3 className="font-heading font-semibold text-foreground">Change profile password</h3></div>
          <ProfilePasswordOtp mode="change" />
        </Card>
        </div>
      )}

      {/* brugo.in leads: operating hours (overnight requests float at opening) + lead emails */}
      <div className="mt-8">
        <h2 className="font-heading font-semibold text-foreground mb-3">Website leads</h2>
        <LeadSettings />
      </div>

      <div className="mt-8">
        <LegalDocuments />
      </div>
    </div>
  );
}
